import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TFarmDataResponse, getFarmData } from "../../services/farm.service";
import SpeedoMetersContainer from "./component/SpeedoMetersContainer";
import AppNotifService, { TAppNotifs } from "../../services/appNotif.service";
import { useRecoilValue } from "recoil";
import { translationAtom } from "../../state/atoms/translations.atom";
import { FylloHeader, NotifLabel } from "react-library";
import { t } from "../../services/locale.service";
import { TBackEvent } from "../../types";
import { Routes } from "../../routes";
import DiseaseSpeedoMeters from "./component/DiseaseSpeedoMeters";
import PestSpeedoMeters from "./component/PestSpeedoMeters";
import PlotSerivce from "../../services/plot.service";
import { Link } from "react-router-dom";
import "./plot.css";
import { CropStageDetails } from "../cropStageDetails";
import Images from "../../assets/images";
/**
 * @component
 * Plot details Screen
 */
const Plot: React.FC = () => {
  const { plotId } = useParams();
  const navigate = useNavigate();

  // farm data states
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [farmData, setFarmData] = useState<TFarmDataResponse>({});

  // app-notif states
  const [isAppNotifLoading, setIsAppNotifLoading] = useState<boolean>(true);
  const [appNotifs, setAppNotifs] = useState<TAppNotifs>([]);
  const [areAppNotifsFiltered, setAreAppNotifsFiltered] =
    useState<boolean>(false);

  // device type
  const [isWeatherUnit, setIsWeatherUnit] = useState<boolean>(false);

  const translations = useRecoilValue(translationAtom);
  const headerTitle = t("MyFarm");

  useEffect(() => {
    window.addEventListener("back-icon-press", handleBack);
    window.addEventListener(
      "open-cropstage-details",
      viewCropStageDetails
    );
    return () => {
      window.removeEventListener("back-icon-press", handleBack);
      window.removeEventListener("open-cropstage-details", viewCropStageDetails);
    };
  }, []);

  useEffect(() => {
    fetchDeviceType();
    fetchAppNotifs();
    fetchFarmData();
  }, []);

  /**
   * API Calls
   */
  const fetchAppNotifs = async () => {
    try {
      setIsAppNotifLoading(true);
      const appNotifs = await AppNotifService.getLatestFieldData(plotId!, 0);
      setAppNotifs(appNotifs);

      const filteredAppNotifs = filterAppNotifs(appNotifs);
      console.log(filteredAppNotifs?.length);
    } catch (error) {
      console.error("app notif fail", error);
    } finally {
      setIsAppNotifLoading(false);
    }
  };

  const fetchFarmData = async () => {
    try {
      setIsLoading(true);
      const farmData = await getFarmData(plotId!);

      setFarmData(farmData);
    } catch (error) {
      console.error("error farmdata", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeviceType = async () => {
    try {
      const response = await PlotSerivce.getDeviceType(plotId!);
      const isWeatherUnit =
        Boolean(response?.device?.deviceType) &&
        !["IRRIGATION_UNIT", "NERO_UNIT"].includes(
          response?.device?.deviceType
        );

      setIsWeatherUnit(isWeatherUnit);
    } catch (error) {
      console.error(error);
    }
  };

  const filterAppNotifs = (alerts: TAppNotifs) => {
    if (!farmData?.soilMoisture1?.description) return alerts;

    if (alerts.length > 0 && !areAppNotifsFiltered) {
      const filteredAlerts = alerts.filter((alert) => {
        // irrigation alerts
        if (alert.notifTypeId === 1) {
          const descriptionSM1 =
            farmData.soilMoisture1.description.toLowerCase();
          const descriptionSM2 = (
            farmData.soilMoisture2?.description ?? ""
          ).toLowerCase();

          const isSM1Wet =
            descriptionSM1.includes("wet") ||
            descriptionSM1.includes("adequate");
          const isSM2Wet =
            descriptionSM2.includes("wet") ||
            descriptionSM2.includes("adequate");

          return !(isSM1Wet && isSM2Wet);
        }

        return true;
      });

      setAreAppNotifsFiltered(true);
      return filteredAlerts;
    }

    return [];
  };

  const handleBack = (e: Event) => {
    const event = e as TBackEvent;
    const eventKey = event.detail;
    if (eventKey === headerTitle) {
      navigate(Routes.home);
    }
  };

  const viewCropStageDetails = (e: Event) => {
    const event = e as Event & { detail: CropStageDetails };
    navigate(Routes.cropStageDetails.replace(":plotId", plotId!), { state: { cropStageDetails: event.detail } });
  };

  if (isLoading || Boolean(translations["LocalesNotFetchedYet"])) {
    return <div data-testid={TestIds.plotDetailsLoader}> {t("Loading")} </div>;
  }

  const hasSensorData = Object.keys(farmData ?? {}).reduce((prev, curr) => {
    return Sensors.includes(curr) || prev;
  }, false);

  const hasInsights = Object.keys(farmData ?? {}).reduce((prev, curr) => {
    return Insights.includes(curr) || prev;
  }, false);

  const sprayUrl = Routes.spray.replace(":plotId", plotId!);
  const fertigationUrl = Routes.fertigation.replace(":plotId", plotId!);

  return (
    <div className="overflow-hidden">
      <FylloHeader headerTitle={headerTitle} hasBackIcon />

      <div className="carousel-container m-t-2">
        <div className="empty-block"></div>
        <div className="carousel-card">
          <NotifLabel plotId={plotId} />
        </div>

        <Link className="carousel-shadow-card carousel-card text-decoration-none m-2" to={sprayUrl}>
          <img className="carousel-card-image" src={Images.Spray} alt="Spray Image" />
          {t("SprayTimings")}
        </Link>

        <Link className="text-decoration-none m-2 carousel-shadow-card carousel-card" to={fertigationUrl}>
          <img className="carousel-card-image" src={Images.Fertigation} alt="Fertigation Image" />
          {t("fertigation")}
        </Link>

        <div className="empty-block"></div>
      </div>

      <>
        {/* appnotifs */}
        <div data-testid={TestIds.appNotifContainer} style={{ display: "none" }}>
          {isAppNotifLoading ? t("Loading") : t("loaded")} - {appNotifs.length}
        </div>

        {!(hasInsights || hasSensorData) && (
          <div data-testid={TestIds.noFarmDataContainer}>{t("No_Data_Found")}</div>
        )}

        {hasSensorData && (
          <SpeedoMetersContainer
            plotId={plotId!}
            farmData={farmData}
            speedoMeterFor="sensor"
          />
        )}

        {isWeatherUnit && <DiseaseSpeedoMeters plotId={plotId!} />}
        {isWeatherUnit && <PestSpeedoMeters plotId={plotId!} />}

        {hasInsights && (
          <SpeedoMetersContainer
            plotId={plotId!}
            farmData={farmData}
            speedoMeterFor="insight"
          />
        )}
      </>
    </div>
  );
};

export default Plot;

export const TestIds = {
  plotDetailsLoader: "plot-details-loader",
  noFarmDataContainer: "no-farm-data",
  appNotifContainer: "app-notif-container",
  speedoMeterContainer: "speedo-meter-for-:speedoMeterFor",
};

// constants
export const Sensors = [
  "airHumidity",
  "airPressue",
  "airPressure",
  "airTemp",
  "leafWetness",
  "lightIntensity",
  "rainFall",
  "soilMoisture1",
  "soilMoisture2",
  "moisture1",
  "moisture2",
  "soilTemp",
  "windSpeed",
];

export const Insights = ["deltaT", "VPD"];
